import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Layers, Loader2 } from 'lucide-react';
import { PermissionProvider, usePermissions } from '../contexts/PermissionContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/axiosConfig';
import AppPageShell from '../components/layout/AppPageShell';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import ValueSchemaEditorForm from '../components/training/ValueSchemaEditorForm';
import AdminAttributeTypesSection from '../components/training/admin/AdminAttributeTypesSection';
import AdminAssessmentSetsSection from '../components/training/admin/AdminAssessmentSetsSection';

function AdminContent() {
  const { t } = useLanguage();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [scenariosError, setScenariosError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [types, setTypes] = useState([]);
  const [sets, setSets] = useState([]);
  const [scenarios, setScenarios] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setScenariosError(null);
    const results = await Promise.allSettled([
      api.get('/api/admin/periodization-catalog/value-schemas'),
      api.get('/api/admin/periodization-catalog/attribute-types'),
      api.get('/api/admin/periodization-catalog/assessment-sets'),
      api.get('/api/admin/periodization-catalog/plan-scenarios'),
    ]);
    const errors = [];
    if (results[0].status === 'fulfilled') {
      setSchemas(results[0].value.data.value_schemas || []);
    } else {
      console.error(results[0].reason);
      setSchemas([]);
      errors.push(t('periodization.valueSchema.tab'));
    }
    if (results[1].status === 'fulfilled') {
      setTypes(results[1].value.data.attribute_types || []);
    } else {
      console.error(results[1].reason);
      setTypes([]);
      errors.push(t('periodization.assessmentManager.typesTab'));
    }
    if (results[2].status === 'fulfilled') {
      setSets(results[2].value.data.assessment_sets || []);
    } else {
      console.error(results[2].reason);
      setSets([]);
      errors.push(t('periodization.assessmentManager.setsTab'));
    }
    if (results[3].status === 'fulfilled') {
      setScenarios(results[3].value.data.scenarios || []);
    } else {
      console.error(results[3].reason);
      setScenarios([]);
      setScenariosError(
        results[3].reason?.response?.data?.error || t('periodization.admin.scenariosLoadError')
      );
    }
    if (errors.length) {
      setLoadError(t('periodization.admin.partialLoadError', { sections: errors.join(', ') }));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  if (!hasPermission('periodization_catalog_manage')) {
    return <Navigate to='/dashboard' replace />;
  }

  const catalogEmpty = !loading && !loadError && schemas.length === 0 && types.length === 0 && sets.length === 0;

  if (loading) {
    return (
      <AppPageShell>
        <div className='flex justify-center items-center gap-2 py-20 text-muted-foreground'>
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
          <span>{t('common.loading')}</span>
        </div>
      </AppPageShell>
    );
  }

  return (
    <AppPageShell>
      <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-bold tracking-tight text-foreground flex items-center gap-2'>
            <Layers className='h-8 w-8 text-primary' aria-hidden />
            {t('periodization.admin.title')}
          </h1>
          <p className='text-muted-foreground mt-1 max-w-2xl text-sm'>{t('periodization.admin.pageLead')}</p>
        </div>
        <div className='flex flex-wrap gap-2 shrink-0'>
          <Button variant='outline' size='sm' asChild>
            <Link to='/training-plan/design'>{t('periodization.admin.backToDesign')}</Link>
          </Button>
          <Button variant='outline' size='sm' asChild>
            <Link to='/admin/coaching-skills'>{t('periodization.admin.backToCoachingAdmin')}</Link>
          </Button>
          <Button variant='outline' size='sm' asChild>
            <Link to='/dashboard'>{t('adminCoaching.backToDashboard')}</Link>
          </Button>
        </div>
      </div>

      {loadError ? (
        <Alert variant='destructive'>
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      ) : null}

      {scenariosError ? (
        <Alert variant='destructive'>
          <AlertTitle>{t('periodization.admin.scenariosTab')}</AlertTitle>
          <AlertDescription>{scenariosError}</AlertDescription>
        </Alert>
      ) : null}

      {catalogEmpty ? (
        <Alert>
          <AlertTitle>{t('periodization.admin.emptyTitle')}</AlertTitle>
          <AlertDescription>{t('periodization.admin.emptyHint')}</AlertDescription>
        </Alert>
      ) : null}

      <Tabs defaultValue='schemas' className='space-y-4'>
        <TabsList className='flex flex-wrap h-auto gap-1'>
          <TabsTrigger value='schemas'>{t('periodization.valueSchema.tab')}</TabsTrigger>
          <TabsTrigger value='types'>{t('periodization.assessmentManager.typesTab')}</TabsTrigger>
          <TabsTrigger value='sets'>{t('periodization.assessmentManager.setsTab')}</TabsTrigger>
          <TabsTrigger value='scenarios'>{t('periodization.admin.scenariosTab')}</TabsTrigger>
        </TabsList>

        <TabsContent value='schemas' className='mt-0'>
          <ValueSchemaEditorForm
            schemas={schemas}
            onReload={load}
            saving={saving}
            setSaving={setSaving}
            mode='admin'
            allowEditGlobal
          />
        </TabsContent>

        <TabsContent value='types' className='mt-0'>
          <AdminAttributeTypesSection
            types={types}
            schemas={schemas}
            onReload={load}
            saving={saving}
            setSaving={setSaving}
          />
        </TabsContent>

        <TabsContent value='sets' className='mt-0'>
          <AdminAssessmentSetsSection
            sets={sets}
            types={types}
            onReload={load}
            saving={saving}
            setSaving={setSaving}
          />
        </TabsContent>

        <TabsContent value='scenarios' className='mt-0'>
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>{t('periodization.admin.scenariosTab')}</CardTitle>
              <CardDescription>{t('periodization.admin.scenariosHint')}</CardDescription>
            </CardHeader>
            <CardContent>
              {scenarios.length === 0 ? (
                <p className='text-sm text-muted-foreground'>{t('periodization.admin.emptyTitle')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('periodization.admin.colName')}</TableHead>
                      <TableHead>{t('periodization.admin.colKey')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scenarios.map((sc) => (
                      <TableRow key={sc.id}>
                        <TableCell className='font-medium'>{sc.name}</TableCell>
                        <TableCell className='font-mono text-xs text-muted-foreground'>{sc.scenario_key}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AppPageShell>
  );
}

export default function PeriodizationCatalogAdminPage() {
  return (
    <PermissionProvider>
      <AdminContent />
    </PermissionProvider>
  );
}
